"use client"
import React, { createContext, useState, useContext, useEffect, ReactNode } from "react"
import { VideoContext } from "~/components/twilio/VideoProvider"
import type { Participant } from "@zoom/videosdk"
import { RecordingStatus } from "~/components/twilio/VideoProvider"
import { MessageType } from "~/components/template/video/Meet"
import { DATA_KEY } from "~/composable/useCallVideoStateZoom"

interface ParticipantsContextType {
  participants: Participant[]
  setSelectParticipant: (p: Participant | null) => void
  selectParticipant: Participant | null
  dominantSpeakerId: number | null
  handleSelectParticipant: (user: Participant) => void
  networkQualities: Record<string, Record<string, number>>
  connectionState: ZoomConnectionState
  toggleRecording: () => Promise<RecordingStatus>
  statusRecording: RecordingStatus
  isRecording: boolean
  setStatusRecording: any
}

export const ParticipantsContext = createContext<ParticipantsContextType | null>(null)

let lastActiveUserId: number | null = null
let debounceTimeout: NodeJS.Timeout | null = null

export const CONNECTION_STATE = {
  CONNECTED: "Connected",
  RECONNECTING: "Reconnecting",
  DISCONNECTED: "Disconnected",
  CONNECTING: "Connecting",
} as const

type ZoomConnectionState = typeof CONNECTION_STATE[keyof typeof CONNECTION_STATE]

interface ParticipantsProviderProps {
  children: ReactNode
}

interface ActiveSpeakerPayload {
  userId: number
}

interface NetworkQualityChangePayload {
  userId: string
  level: number
  type: string
}

export function ParticipantsProvider({ children }: ParticipantsProviderProps) {
  const { client, sendData } = useContext(VideoContext)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [selectParticipant, setSelectParticipant] = useState<Participant | null>(null)
  const [dominantSpeakerId, setDominantSpeaker] = useState<number | null>(null)
  const [networkQualities, setNetworkQualities] = useState<Record<string, Record<string, number>>>(
    {}
  )
  const [connectionState, setConnectionState] = useState<ZoomConnectionState>(
    CONNECTION_STATE.CONNECTED
  )
  const [statusRecording, setStatusRecording] = useState<RecordingStatus>(RecordingStatus.Stopped)
  const isRecording = statusRecording === RecordingStatus.Recording
  const currentUserId = client?.getCurrentUserInfo()?.userId

  const getCloudRecordingStatus = () => {
    const cloudRecording = client.getRecordingClient()
    const status = cloudRecording.getCloudRecordingStatus()
    setStatusRecording(status)
  }

  const toggleRecording = async () => {
    if (!client) return
    const cloudRecording = client.getRecordingClient()
    const status = cloudRecording.getCloudRecordingStatus()
    if (!cloudRecording.canStartRecording() && !client?.getCurrentUserInfo()?.isHost) return
    try {
      if (status === RecordingStatus.Recording) {
        await cloudRecording.stopCloudRecording()
      } else {
        await cloudRecording.startCloudRecording()
      }
      const newStatus = cloudRecording.getCloudRecordingStatus()
      setStatusRecording(newStatus)
      return newStatus
    } catch (error) {
      console.error("Error toggling recording:", error)
    }
  }

  const updateParticipantList = async () => {
    if (!client) return
    const participants = client.getAllUser() ?? []
    setParticipants(participants)
  }

  useEffect(() => {
    if (!client) return

    // When someone joins the room
    client.on("user-added", updateParticipantList)

    // When someone leaves the room
    const handleUserRemoved = ([{ userId, isHost }]: [{ userId: number; isHost: boolean }]) => {
      if (selectParticipant?.userId === userId) {
        setSelectParticipant(null)
      }
      if (dominantSpeakerId === userId) {
        setDominantSpeaker(null)
      }
      if (isHost) {
        setStatusRecording(RecordingStatus.Stopped)
      }
      updateParticipantList()
    }
    client.on("user-removed", handleUserRemoved)

    // When someone turn on/off video or mic
    client.on("user-updated", updateParticipantList)

    // When someone speaks loudly in the room
    const handleActiveSpeaker = (speakers: ActiveSpeakerPayload[]) => {
      if (!speakers || speakers.length === 0) {
        setDominantSpeaker(null)
        return
      }
      const [dominant] = speakers
      if (dominant.userId === dominantSpeakerId || dominant.userId === currentUserId) return

      if (dominant.userId !== lastActiveUserId) {
        clearTimeout(debounceTimeout)
        debounceTimeout = setTimeout(() => {
          lastActiveUserId = dominant.userId
          setDominantSpeaker(dominant.userId)
        }, 300)
      }
    }
    client.on("active-speaker", handleActiveSpeaker)

    // Get network quality of participants in the room
    const handleNetworkQualityChange = (payload: NetworkQualityChangePayload) => {
      const { userId, level, type } = payload
      setNetworkQualities((prev) => ({ ...prev, [userId]: { ...prev[userId], [type]: level } }))
    }
    client.on("network-quality-change", handleNetworkQualityChange)

    const handleConnectionChange = (payload: { state: string }) => {
      setConnectionState(payload.state as ZoomConnectionState)
    }
    client.on("connection-change", handleConnectionChange)

    updateParticipantList()
    const cloudRecording = client.getRecordingClient()
    // HungHV: agent first join is host
    if (cloudRecording.canStartRecording() && client?.getCurrentUserInfo()?.isHost) {
      ;(async () => {
        await toggleRecording()
        const cloudRecording = client.getRecordingClient()
        const status = cloudRecording.getCloudRecordingStatus()
        sendData({
          type: MessageType.NOTIFY,
          setUpdateTime: new Date().getTime(),
          data: { [DATA_KEY.RECORD]: status },
        })
      })()
    } else {
      // synchronize the current recording state of objects other than the host
      getCloudRecordingStatus()
    }

    return () => {
      client.off("user-added", updateParticipantList)
      client.off("user-removed", handleUserRemoved)
      client.off("user-updated", updateParticipantList)
      client.off("active-speaker", handleActiveSpeaker)
      client.off("network-quality-change", handleNetworkQualityChange)
      client.off("connection-change", handleConnectionChange)
    }
  }, [client])

  const handleSelectParticipant = (user: Participant) => {
    if (!user || user.userId === currentUserId) {
      return
    }
    // HungHV: if a higher priority operation occurs, the other types need to be cached to null.
    setDominantSpeaker(null)
    if (selectParticipant?.userId === user.userId) {
      setSelectParticipant(null)
    } else {
      setSelectParticipant(user)
    }
  }

  return (
    <ParticipantsContext.Provider
      value={{
        participants,
        setSelectParticipant,
        selectParticipant,
        dominantSpeakerId,
        handleSelectParticipant,
        networkQualities,
        connectionState,
        toggleRecording,
        statusRecording,
        isRecording,
        setStatusRecording,
      }}
    >
      {children}
    </ParticipantsContext.Provider>
  )
}
